import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import {
    CustomFieldDefinitionType,
    IContact,
    IContactCustomField,
    ICustomFieldDefinition,
    IDropdown,
} from '@dxp/core/models';
import { LoggerService } from '@dxp/core/services';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, distinctUntilChanged, tap } from 'rxjs';
import { TelXLDropdownComponent } from '@dxp/elements';
import { ValidateContactFieldDirective } from '@dxp/core/directives';
import { SimpleEmailValidator, UKNumberValidator } from '@dxp/core/validators';
import { NgClass } from '@angular/common';
import {
    ContactDetailsService,
    FormAction,
} from '../contact-details/contact-details.service';
import { ContactDirectoriesApiService } from '../services/contact-directories.api.service';

@Component({
    selector: 'contact-directories-details-form',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        ReactiveFormsModule,
        NgbTooltip,
        TelXLDropdownComponent,
        ValidateContactFieldDirective,
    ],
    templateUrl: './contact-details-form.component.html',
    styleUrl: './contact-details-form.component.scss',
})
export class ContactDirectoriesDetailsFormComponent implements OnInit {
    readonly #destroyRef = inject(DestroyRef);
    readonly #fb = inject(FormBuilder);
    readonly #contactDetailsService = inject(ContactDetailsService);
    readonly #contactDirectoriesApiService = inject(
        ContactDirectoriesApiService,
    );
    readonly #logger = inject(LoggerService);

    readonly #isEditing = signal<boolean>(false);

    contact = input.required<IContact>();
    updated = output<IContact>();

    readonly contact$ = toObservable(this.contact);

    readonly customFieldDefinitions = signal<
        ICustomFieldDefinition[] | undefined
    >(undefined);
    readonly editing = computed<boolean>(() => this.#isEditing());

    contactForm!: FormGroup;
    #initialFormValue!: IContact;

    ngOnInit() {
        this.#contactDetailsService.isEditing$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(isEditing => {
                this.#isEditing.set(isEditing);
                this.#initForm();
            });

        this.#contactDetailsService.formAction$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((action: FormAction) => {
                if (action === 'save') {
                    this.submitForm();
                } else if (action === 'cancel') {
                    this.#contactDetailsService.updateEditing(false);
                }
            });

        this.contact$
            .pipe(
                distinctUntilChanged(),
                tap(_ => {
                    this.#initForm();
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();

        this.#contactDirectoriesApiService
            .fetchCustomFields()
            .pipe(
                tap(customerFieldDefinitions => {
                    this.customFieldDefinitions.set(customerFieldDefinitions);

                    this.#initForm();
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    #initForm() {
        if (this.customFieldDefinitions() === undefined) return;

        this.contactForm = this.#fb.group(
            {
                fullName: [
                    {
                        value: this.contact().fullName,
                        disabled: !this.editing(),
                    },
                ],
                emailAddress: [
                    {
                        value: this.contact().emailAddress ?? '',
                        disabled: !this.editing(),
                    },
                    [SimpleEmailValidator()],
                ],
                phoneNumber: [
                    {
                        value: this.contact().phoneNumber ?? '',
                        disabled: !this.editing(),
                    },
                    [UKNumberValidator()],
                ],
                customFields: this.#fb.array(
                    (this.customFieldDefinitions() ?? []).map(cfd => {
                        const existingCustomField =
                            this.contact().customFields.find(
                                cf => cf.fieldDefinitionId === cfd.id,
                            );

                        const fieldGroup = this.#fb.group({
                            fieldDefinitionId: [
                                {
                                    value: cfd.id,
                                    disabled: !this.editing(),
                                },
                            ],
                            value: [
                                {
                                    value: existingCustomField?.value ?? '',
                                    disabled: !this.editing(),
                                },
                            ],
                        } as Record<
                            string,
                            {
                                value: string | number;
                                disabled: boolean;
                            }[]
                        >);

                        return fieldGroup;
                    }),
                ),
            },
            { validators: this.#atLeastOneContactRequired() },
        );

        combineLatest([
            this.contactForm.valueChanges.pipe(
                tap(() => this.#checkIfFormChanged()),
            ),
            this.contactForm.statusChanges,
        ])
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(() => {
                this.#contactDetailsService.updateCanSave(
                    this.contactForm.valid && this.#checkIfFormChanged(),
                );
            });

        this.#initialFormValue = this.contactForm.getRawValue();
    }

    #checkIfFormChanged(): boolean {
        const currentFormValue = this.contactForm.getRawValue();
        return (
            JSON.stringify(this.#initialFormValue) !==
            JSON.stringify(currentFormValue)
        );
    }

    submitForm() {
        if (this.contactForm.valid) {
            const contact: IContact = {
                id: this.contact().id,
                fullName: this.contactForm.get('fullName')?.value,
                emailAddress: this.contactForm.get('emailAddress')?.value,
                phoneNumber: this.contactForm.get('phoneNumber')?.value,
                directory: this.contact().directory,
                customFields: this.contactForm
                    .get('customFields')
                    ?.value.filter(
                        (field: IContactCustomField) => field.value.length > 0,
                    ),
            };

            this.#contactDirectoriesApiService
                .updateContact(contact)
                .subscribe({
                    next: () => {
                        this.#contactDetailsService.updateEditing(false);
                        this.updated.emit(contact);
                    },
                    error: () =>
                        this.#logger.error('Failed to update contact', contact),
                });
        }
    }

    get customFields(): FormArray {
        return this.contactForm.get('customFields') as FormArray;
    }

    getCustomFieldLabel(fieldDefinitionId: number): string {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.label : 'Unknown Field';
    }

    getSelectedOption(fieldDefinitionId: number): string {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        return customField ? customField.get('value')?.value : '';
    }

    getCustomFieldType(
        fieldDefinitionId: number,
    ): CustomFieldDefinitionType | 'Unknown' {
        const definition = this.#getCustomField(fieldDefinitionId);
        return definition ? definition.type : 'Unknown';
    }

    onOptionSelected(fieldDefinitionId: number, selectedValue: string) {
        const customField = this.customFields.controls.find(
            cf => cf.get('fieldDefinitionId')?.value === fieldDefinitionId,
        );

        if (customField) {
            customField.get('value')?.setValue(selectedValue);
            customField.get('value')?.markAsDirty();
            customField.get('value')?.markAsTouched();
            this.contactForm.updateValueAndValidity();
        }
    }

    #getCustomField(
        fieldDefinitionId: number,
    ): ICustomFieldDefinition | undefined {
        return (this.customFieldDefinitions() ?? []).find(
            def => def.id === fieldDefinitionId,
        );
    }

    getCustomFieldOptions(fieldDefinitionId: number): IDropdown<string>[] {
        const definition = this.#getCustomField(fieldDefinitionId);
        const options = definition ? (definition.options ?? []) : [];

        return [...options.map(o => ({ label: o, value: o, disabled: false }))];
    }

    #atLeastOneContactRequired(): ValidatorFn {
        return (form: AbstractControl): ValidationErrors | null => {
            const email = form.get('emailAddress')?.value?.trim();
            const phoneNumber = form.get('phoneNumber')?.value?.trim();

            if (!email && !phoneNumber) {
                return { atLeastOneContactRequired: true };
            }

            return null;
        };
    }
}
